package org.example.project.screens_cus

import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.dashboard.PopupEngineCard
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.border
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.boxSizing
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.lineHeight
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.textAlign
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun GeneratorConfirmPopup(
    count: Int,
    itemLabel: String,
    onCancel: () -> Unit,
    onConfirm: () -> Unit
) {
    PopupEngineCard(
        windowKey = "generator-confirm-$itemLabel",
        zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
        widthPx = 520,
        heightPx = 260,
        minWidthPx = 520,
        minHeightPx = 260,
        initialX = 420.0,
        initialY = 180.0,
        resizable = false,
        viewportAnchored = true,
        showCloseIcon = false,
        contentBackground = DashboardTheme.POPUP_BG,
        frameBackground = null,
        onClose = onCancel
    ) {
        GeneratorPopupContent {
            GeneratorPopupTitle("Anlegen bestätigen")
            GeneratorPopupText("Sie sind dabei, $count $itemLabel anzulegen.")
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    gap(10.px)
                    width(100.percent)
                }
            }) {
                LumaticaButton("Abbrechen", onClick = onCancel)
                LumaticaButton("OK", onClick = onConfirm, variant = LumaticaButtonVariant.Action)
            }
        }
    }
}

@Composable
internal fun GeneratorProgressPopup(
    count: Int,
    itemLabel: String,
    progress: Int,
    resultMessage: String = "",
    onClose: (() -> Unit)? = null
) {
    PopupEngineCard(
        windowKey = "generator-progress-$itemLabel",
        zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
        widthPx = 560,
        heightPx = 300,
        minWidthPx = 560,
        minHeightPx = 300,
        initialX = 400.0,
        initialY = 170.0,
        resizable = false,
        viewportAnchored = true,
        showCloseIcon = false,
        contentBackground = DashboardTheme.POPUP_BG,
        frameBackground = null,
        onClose = {}
    ) {
        GeneratorPopupContent {
            GeneratorPopupTitle(if (resultMessage.isBlank()) "$count $itemLabel werden erzeugt" else "Erzeugung abgeschlossen")
            GeneratorPopupText(
                resultMessage.ifBlank {
                    "Die Datensätze werden gespeichert und die Liste wird anschließend aktualisiert."
                }
            )
            Div({
                style {
                    width(100.percent)
                    height(12.px)
                    borderRadius(999.px)
                    backgroundColor(org.jetbrains.compose.web.css.rgba(255, 255, 255, 0.08))
                    border(1.px, LineStyle.Solid, org.jetbrains.compose.web.css.rgba(255, 255, 255, 0.10))
                    overflow("hidden")
                }
            }) {
                Div({
                    style {
                        width(progress.coerceIn(0, 100).percent)
                        height(100.percent)
                        backgroundColor(DashboardTheme.ACCENT)
                        property("transition", "width 180ms ease")
                    }
                })
            }
                Span({
                    style {
                        color(DashboardTheme.TEXT_MUTED)
                        fontSize(13.px)
                        fontWeight("700")
                    }
                }) {
                    Text(
                        if (resultMessage.isBlank()) {
                            "Erzeuge $itemLabel... ${progress.coerceIn(0, 100)}%"
                        } else {
                            "$itemLabel erzeugt"
                        }
                    )
                }
            if (resultMessage.isNotBlank() && onClose != null) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        width(100.percent)
                    }
                }) {
                    LumaticaButton("OK", onClick = onClose, variant = LumaticaButtonVariant.Action)
                }
            }
        }
    }
}

@Composable
private fun GeneratorPopupContent(content: @Composable () -> Unit) {
    Div({
        style {
            width(100.percent)
            height(100.percent)
            padding(24.px)
            boxSizing("border-box")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            gap(18.px)
            color(DashboardTheme.TEXT_LIGHT)
            textAlign("center")
        }
    }) {
        content()
    }
}

@Composable
private fun GeneratorPopupTitle(text: String) {
    P({
        style {
            margin(0.px)
            color(DashboardTheme.TEXT_LIGHT)
            fontSize(24.px)
            fontWeight("800")
            lineHeight("1.15")
            textAlign("center")
        }
    }) { Text(text) }
}

@Composable
private fun GeneratorPopupText(text: String) {
    P({
        style {
            margin(0.px)
            color(DashboardTheme.TEXT_MUTED)
            fontSize(15.px)
            lineHeight("1.55")
            textAlign("center")
        }
    }) { Text(text) }
}
